<template>
    <div class="group border-b border-border p-4 block text-ink">
        <div class="flex justify-between items-start gap-6 w-full">
            <div class="min-w-0 grow text-left">
                <p class="scaffold-body-small mb-2 break-words text-ink-muted">{{ badge }}</p>
                <div v-if="isNote" ref="noteContent" :class="{ 'max-h-15 overflow-hidden': !isExpanded }">
                    <MarkdownText :text="content" class="body-small break-words text-ink" />
                </div>
                <p v-else class="body-small text-ink">
                    {{ content }}
                </p>
                <BaseButton v-if="needsTruncation" theme="link" class="mt-2 body-small" :aria-expanded="isExpanded" @click.stop="toggleExpanded">
                    {{ isExpanded ? t("connections.contextRow.showLess") : t("connections.contextRow.showMore") }}
                </BaseButton>
            </div>
            <div class="flex shrink-0 self-stretch items-center gap-3">
                <BaseLink
                    v-if="item.return_url"
                    :href="item.return_url"
                    :aria-label="t('connections.contextRow.viewItem', { view: t('connections.contextRow.view'), title: item.title })"
                    class="base-btn-neutral base-btn-sm opacity-0 group-hover:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100">
                    {{ t("connections.contextRow.view") }}
                </BaseLink>
                <BaseDropdownMenu
                    v-if="isEditable"
                    v-model:open="isMenuOpen"
                    :sections="menuSections"
                    side="left"
                    :side-offset="10"
                    :aria-label="t('connections.contextRow.noteActions')"
                    @select="handleMenuSelect">
                    <template #trigger>
                        <BaseButton ref="menuTrigger" theme="tertiary" icon-only :aria-label="t('connections.contextRow.moreNoteOptions')" @click.stop>
                            <i class="bi bi-three-dots text-xl/none" aria-hidden="true" />
                        </BaseButton>
                    </template>
                </BaseDropdownMenu>
                <BaseButton
                    v-else-if="isNote"
                    ref="menuTrigger"
                    theme="neutral"
                    size="sm"
                    class="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100"
                    @click.stop="isConfirmDeleteDialogOpen = true">
                    {{ t("common.delete") }}
                </BaseButton>
            </div>
        </div>
    </div>
    <ConfirmationDialog
        v-if="isNote"
        v-model:open="isConfirmDeleteDialogOpen"
        :return-focus-to="menuTrigger"
        :title="t('connections.contextRow.deleteDialog.title')"
        :text="t('connections.contextRow.deleteDialog.text')"
        :confirm-label="t('common.delete')"
        @confirm="handleDelete" />
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import { useResizeObserver } from "@vueuse/core";
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDropdownMenu, { type DropdownMenuItem, type DropdownMenuSection } from "~vue/components/BaseDropdownMenu.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { formatDate } from "~vue/utils/formatDate";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, nextTick, ref, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

// Constants
const TIMELINE_ITEM_TYPE = {
    MENTION: "mention",
    NOTE: "note",
} as const;

// Types
export interface TimelineItem {
    id: string;
    type: "mention" | "note";
    title?: string;
    content?: string;
    created_at: string;
    is_llm_generated?: boolean;
    generated_by?: string;
    return_url?: string;
}

// Props
const props = defineProps<{
    item: TimelineItem;
}>();

const { t } = useI18n();

// State
const isExpanded = ref(false);
const isConfirmDeleteDialogOpen = ref(false);
const isMenuOpen = ref(false);
const menuTrigger = useTemplateRef("menuTrigger");

// Computed properties
const isMention = computed(() => props.item.type === TIMELINE_ITEM_TYPE.MENTION);
const isNote = computed(() => props.item.type === TIMELINE_ITEM_TYPE.NOTE);
const isLlmGenerated = computed(() => isNote.value && props.item.is_llm_generated);
const isWorkiq = computed(() => isNote.value && props.item.generated_by === "workiq");
// WorkIQ notes are auto-generated enrichment, not the user's own words — deletable but not editable.
const isEditable = computed(() => isNote.value && !isLlmGenerated.value && !isWorkiq.value);

const badge = computed(() => {
    const date = formatDate(props.item.created_at, "date");
    if (isMention.value) {
        return t("connections.contextRow.badge.conversation", { date });
    }
    if (isWorkiq.value) {
        return t("connections.contextRow.badge.workiq", { date });
    }
    return isLlmGenerated.value ? t("connections.contextRow.badge.nadiaNote", { date }) : t("connections.contextRow.badge.yourNote", { date });
});

const content = computed(() => {
    if (isMention.value) {
        return props.item.title ?? "";
    }
    return props.item.content ?? "";
});

// Collapsed notes show three lines; "See more" only appears when that actually hides text.
const noteElement = useTemplateRef<HTMLElement>("noteContent");
const isOverflowing = ref(false);
function measureOverflow() {
    const element = noteElement.value;
    if (element && !isExpanded.value) {
        isOverflowing.value = element.scrollHeight > element.clientHeight + 1;
    }
}
useResizeObserver(noteElement, measureOverflow);
watch([noteElement, content], () => nextTick(measureOverflow), { immediate: true });
const needsTruncation = computed(() => isNote.value && (isOverflowing.value || isExpanded.value));

const menuSections = computed<DropdownMenuSection[]>(() => [
    {
        id: "actions",
        items: [{ id: "edit", type: "button", label: t("common.edit"), icon: "bi bi-pencil" }],
    },
    {
        id: "danger",
        items: [{ id: "delete", type: "button", label: t("common.delete"), icon: "bi bi-trash", theme: "danger" }],
    },
]);

const deleteUrl = computed(() => `/api/connections/notes/${props.item.id}/`);

// API
const { execute: executeDelete, onFetchResponse: onDeleteResponse } = useFetch(deleteUrl, { immediate: false }).delete();

// Event handlers
const toggleExpanded = () => {
    isExpanded.value = !isExpanded.value;
};

const handleDelete = () => {
    logUserInteraction(AmplitudeEventNames.CONNECTION_DELETE_NOTE_BUTTON_CLICKED);
    executeDelete();
};

const handleEditNote = () => {
    isMenuOpen.value = false;
    logUserInteraction(AmplitudeEventNames.CONNECTION_EDIT_NOTE_BUTTON_CLICKED);
    router.visit(`/people/notes/${props.item.id}/edit/`);
};

const handleMenuSelect = (selectedItem: DropdownMenuItem) => {
    isMenuOpen.value = false;
    if (selectedItem.id === "edit") {
        handleEditNote();
    } else if (selectedItem.id === "delete") {
        isConfirmDeleteDialogOpen.value = true;
    }
};

// API response handlers
onDeleteResponse(() => {
    logUserInteraction(AmplitudeEventNames.CONNECTION_NOTE_DELETED);
    router.reload({ only: ["timeline"] });
});
</script>
